import { PipelineItem, ImportMapping } from './types';
import { parseAsLocalDate } from '@/utils/dateHelpers';

export const getStatusColor = (status: PipelineItem['status']) => {
  const colors = {
    New: 'bg-blue-500',
    'In Review': 'bg-yellow-500',
    Underwriting: 'bg-orange-500',
    Approved: 'bg-green-500',
    Denied: 'bg-red-500',
    Closed: 'bg-gray-500'
  };
  return colors[status] || 'bg-gray-500';
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export const formatDate = (dateString: string): string => {
  return parseAsLocalDate(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const getDefaultFieldMappings = (): ImportMapping[] => [
  { csvColumn: '', systemField: 'borrowerName', required: true, dataType: 'text' },
  { csvColumn: '', systemField: 'loanAmount', required: true, dataType: 'currency' },
  { csvColumn: '', systemField: 'loanType', required: true, dataType: 'text' },
  { csvColumn: '', systemField: 'propertyAddress', required: true, dataType: 'text' },
  { csvColumn: '', systemField: 'loanOfficer', required: true, dataType: 'text' },
  { csvColumn: '', systemField: 'status', required: false, dataType: 'text' },
  { csvColumn: '', systemField: 'applicationDate', required: false, dataType: 'date' },
  { csvColumn: '', systemField: 'creditScore', required: false, dataType: 'number' },
  { csvColumn: '', systemField: 'propertyValue', required: false, dataType: 'currency' },
  { csvColumn: '', systemField: 'downPayment', required: false, dataType: 'currency' },
  { csvColumn: '', systemField: 'interestRate', required: false, dataType: 'number' },
  { csvColumn: '', systemField: 'closingDate', required: false, dataType: 'date' }
];

export const parseCSV = (csvText: string): string[][] => {
  const lines = csvText.split('\n');
  const result: string[][] = [];
  
  for (const line of lines) {
    if (line.trim()) {
      // Simple CSV parsing - in production, use a proper CSV parser
      const fields = line.split(',').map(field => field.trim().replace(/^"|"$/g, ''));
      result.push(fields);
    }
  }
  
  return result;
};

export const validateImportData = (data: string[][], mappings: ImportMapping[]): string[] => {
  const errors: string[] = [];
  const requiredFields = mappings.filter(m => m.required && m.csvColumn);
  
  if (data.length < 2) {
    errors.push('File must contain at least a header row and one data row');
    return errors;
  }
  
  const headers = data[0];
  
  // Check if all required fields are mapped
  for (const field of requiredFields) {
    if (!headers.includes(field.csvColumn)) {
      errors.push(`Required field "${field.systemField}" is mapped to column "${field.csvColumn}" which doesn't exist in the file`);
    }
  }
  
  return errors;
};

export const filterPipelineItems = (items: PipelineItem[], searchTerm: string, statusFilter: string): PipelineItem[] => {
  return items.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.borrowerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.loanOfficer.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
};